import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, Star, Calendar, Image, MapPin, Clock, Car,
  Shield, ShieldCheck, Lock, MessageSquare, CheckCircle, Phone,
  Briefcase, Award, Crown, Coins, Unlock, AlertCircle, User
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import VerificationBadge from '../components/VerificationBadge';
import RevealPhoneButton from '../components/RevealPhoneButton';
import { BACKEND_URL } from '../config/api';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import { isDayRateEligible } from '../utils/workerType';

/**
 * TradesPersonProfile - Clean credit-gated profile viewing
 * 
 * Journey:
 * 1. FREE: Basic preview (name initial, trade, ratings, limited bio)
 * 2. UNLOCK PROFILE (1 credit): Full name, full bio, all reviews, all photos, credentials
 * 3. START CHAT (2 credits): Begin conversation (subsequent messages free)
 * 
 * NEVER REVEALED: Phone, email, personal contact details (all contact through platform)
 */

// Insufficient Credits Prompt — FREE MARKETPLACE: no longer rendered.
const InsufficientCreditsPrompt = () => null;

// Locked Content Placeholder
const LockedPlaceholder = ({ message = "Unlock profile to view" }) => (
  <div className="bg-slate-50 rounded-lg p-6 text-center">
    <Lock className="h-6 w-6 text-slate-400 mx-auto mb-2" />
    <p className="text-sm text-slate-500">{message}</p>
  </div>
);

export default function TradesPersonProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [credits, setCredits] = useState(null); // null = not yet loaded; don't render "0 credits" until fetched
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  const startChatInFlight = useRef(false); // concurrency guard — blocks double-taps
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(null); // 'unlock' | 'chat' | null

  // Section refs for icon-tab scroll navigation
  const profileSummaryRef = useRef(null);
  const workDetailsRef = useRef(null);
  const availabilityRef = useRef(null);
  const reviewsRef = useRef(null);
  const chatRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fetchCredits = useCallback(async () => {
    setCreditsLoading(true);
    setCreditsError(false);
    try {
      // Canonical source of truth — SAME endpoint as the dashboard / CreditBalance.
      // Reads the `credits` field on employer_credits and applies founding bonus.
      const response = await axios.get(`${BACKEND_URL}/api/credits/balance`, { withCredentials: true });
      const actions = response.data.costs || {};
      setCredits({
        balance: response.data.effective_credits ?? response.data.credits ?? 0,
        subscription: response.data.subscription || { active: false },
        costs: {
          unlockProfile: actions.view_full_profile ?? 1,
          startChat: actions.start_chat ?? 2,
          // keep on-call premium costs available for future use
          onCallUnlockProfile: actions.on_call_profile_unlock ?? 3,
          onCallStartChat: actions.on_call_chat_start ?? 4,
        },
      });
    } catch (err) {
      // Only flag error when the employer is actually signed in but the call failed.
      // Silent 401s (not logged in / not employer) are handled by fetchCurrentUser.
      if (err.response && err.response.status !== 401 && err.response.status !== 403) {
        setCreditsError(true);
      }
      // Do NOT overwrite an existing valid balance with 0 on transient failure.
    } finally {
      setCreditsLoading(false);
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
      if (response.data.user_type === 'employer') {
        fetchCredits();
      }
    } catch (err) {
      setCurrentUser(null);
    }
  }, [fetchCredits]);

  const fetchProfile = useCallback(async () => {
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tradespeople/profile/${userId}`, { withCredentials: true });
      setProfile(response.data);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCurrentUser();
    fetchProfile();
  }, [fetchCurrentUser, fetchProfile]);

  // One-click reveal profile (free marketplace — no subscription, no credit cost).
  const handleUnlockProfile = async () => {
    setUnlocking(true);
    setShowInsufficientCredits(null);

    try {
      await axios.post(`${BACKEND_URL}/api/credits/use`, {
        action: 'view_full_profile',
        target_id: userId
      }, { withCredentials: true });

      // Refresh profile.
      await Promise.all([fetchProfile(), fetchCredits()]);
      toast.success('Profile fully visible');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || 'Failed to open profile');
    } finally {
      setUnlocking(false);
    }
  };

  // One-click start chat (idempotent — safe against double-taps)
  const handleStartChat = async (intentOverride) => {
    // Hard guard against rapid taps / duplicate fires.
    if (startChatInFlight.current) return;

    // Determine the employer intent so Messaging can auto-open the right form.
    const workerType = profile?.user_info?.worker_type || profile?.worker_type;
    const preferredWorkType = profile?.extended?.preferred_work_type;
    const intent =
      intentOverride ||
      (isDayRateEligible(workerType, preferredWorkType) ? 'offer' : 'quote');

    // If an active conversation already exists according to the profile, just
    // open the thread — never call start-chat, never risk a charge.
    if (profile?.has_active_conversation) {
      navigate('/messages', {
        state: {
          recipient: {
            user_id: userId,
            name: profile?.name,
            user_type: 'tradesperson',
            worker_type: workerType,
            preferred_work_type: preferredWorkType,
          },
          autoOpenIntent: intent,
        },
      });
      return;
    }

    // FREE MARKETPLACE: subscription gate removed. The backend start-chat
    // endpoint is now free and the hiring_access_guard remains the sole
    // authority on who may start chats.

    startChatInFlight.current = true;
    setStartingChat(true);
    setShowInsufficientCredits(null);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/start`,
        { tradespersonId: userId },
        { withCredentials: true },
      );

      if (response.data?.success) {
        await fetchCredits();
        navigate('/messages', {
          state: {
            recipient: {
              user_id: userId,
              name: profile?.name,
              user_type: 'tradesperson',
              worker_type: workerType,
              preferred_work_type: preferredWorkType,
            },
            conversationId: response.data.conversationId,
            autoOpenIntent: intent,
          },
        });
      } else {
        toast.error('Could not start chat — please try again');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        await fetchCurrentUser();
        toast.error(detail?.message || 'You must be signed in as an employer to start a chat');
      } else {
        toast.error(detail?.message || detail || 'Failed to start chat');
      }
    } finally {
      setStartingChat(false);
      startChatInFlight.current = false;
    }
  };

  const handleTopUp = () => {
    // Navigate to employer dashboard with param to open credit purchase modal
    navigate('/employer/dashboard?openCredits=true');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="profile-not-found">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {error ? 'Unable to Load Profile' : 'Profile Not Found'}
          </h2>
          <p className="text-slate-600 mb-6">
            {error || 'This profile may have been removed or is no longer available.'}
          </p>
          <button onClick={() => navigate(-1)} className="btn-primary">Go Back</button>
        </div>
      </div>
    );
  }

  const isEmployerViewing = currentUser?.user_type === 'employer';
  const isUnlocked = profile.profile_unlocked;
  const hasConversation = profile.has_active_conversation;
  const isVerified = profile.is_verified;

  // Engagement model — day-rate vs quote-based. Drives which CTA we show.
  const workerType = profile.user_info?.worker_type || profile.worker_type;
  const preferredWorkType = profile.extended?.preferred_work_type;
  const dayRateEligible = isDayRateEligible(workerType, preferredWorkType);
  const chatIntent = dayRateEligible ? 'offer' : 'quote';
  
  // Display name: full if unlocked, initial if not
  const displayName = profile.user_info?.name || 'Unknown';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900 hidden sm:block">OnsiteHand</h1>
            </div>
          </div>
          
          {/* FREE MARKETPLACE: credit balance pill removed. */}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Icon Tab Navigation — scrolls to sections */}
        <nav
          className="sticky top-[72px] z-40 -mx-4 sm:-mx-6 lg:-mx-8 mb-6 bg-white/90 backdrop-blur-sm border-b border-slate-200"
          data-testid="profile-icon-nav"
          aria-label="Profile sections"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ul className="flex items-center justify-around sm:justify-start sm:gap-6 py-2">
              <li>
                <button
                  type="button"
                  aria-label="View profile summary"
                  onClick={() => scrollToSection(profileSummaryRef)}
                  className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-slate-600 hover:text-orange-600 transition-colors"
                  data-testid="nav-profile-summary"
                >
                  <User className="h-5 w-5" />
                  <span className="text-[10px] font-medium">Profile</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  aria-label="View work details"
                  onClick={() => scrollToSection(workDetailsRef)}
                  className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-slate-600 hover:text-orange-600 transition-colors"
                  data-testid="nav-work-details"
                >
                  <Briefcase className="h-5 w-5" />
                  <span className="text-[10px] font-medium">Work</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  aria-label="View availability"
                  onClick={() => scrollToSection(availabilityRef)}
                  className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-slate-600 hover:text-orange-600 transition-colors"
                  data-testid="nav-availability"
                >
                  <Calendar className="h-5 w-5" />
                  <span className="text-[10px] font-medium">Availability</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  aria-label="View reviews"
                  onClick={() => scrollToSection(reviewsRef)}
                  className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-slate-600 hover:text-orange-600 transition-colors"
                  data-testid="nav-reviews"
                >
                  <Star className="h-5 w-5" />
                  <span className="text-[10px] font-medium">Reviews</span>
                </button>
              </li>
              {isEmployerViewing && (
                <li>
                  <button
                    type="button"
                    aria-label="Start chat"
                    onClick={() => scrollToSection(chatRef)}
                    className="flex flex-col items-center gap-0.5 px-2 py-1.5 text-slate-600 hover:text-orange-600 transition-colors"
                    data-testid="nav-chat"
                  >
                    <MessageSquare className="h-5 w-5" />
                    <span className="text-[10px] font-medium">Chat</span>
                  </button>
                </li>
              )}
            </ul>
          </div>
        </nav>

        {/* Profile Header Card */}
        <div ref={profileSummaryRef} className="card mb-6 scroll-mt-36" data-testid="profile-header">
          <div className="flex flex-col md:flex-row gap-6 mb-6">
            {/* Profile Picture */}
            <div className="flex justify-center md:justify-start">
              <div className="w-32 h-32 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center ring-4 ring-white shadow-lg">
                {profile.user_info?.profile_image ? (
                  <img
                    src={profile.user_info.profile_image.startsWith('http') 
                      ? profile.user_info.profile_image 
                      : `${BACKEND_URL}${profile.user_info.profile_image}`}
                    alt={displayName}
                    className="w-full h-full object-cover"
                    data-testid="profile-image"
                  />
                ) : (
                  <div className="text-5xl font-bold text-slate-400">
                    {displayName.charAt(0)}
                  </div>
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-slate-900 mb-2" data-testid="profile-name">
                {displayName}
              </h2>
              
              <p className="text-xl text-orange-500 font-semibold mb-3" data-testid="profile-trade">
                {profile.trade}
              </p>

              {/* Truthful verification badge driven by verification_summary */}
              <div className="mb-2">
                <VerificationBadge summary={profile.verification_summary} testIdPrefix="profile" />
              </div>
              {(profile.verification_summary === 'verified' || profile.verification_summary === 'partial') && (
                <p className="text-xs text-slate-500 mb-4 leading-snug max-w-md">
                  A verification badge means OnSiteHand has checked the evidence submitted for that specific credential. It is not a guarantee of workmanship, conduct, availability or suitability.
                </p>
              )}
              
              {/* Founder Badge */}
              {profile.grandfather_status && profile.grandfather_label && (
                <div className="mb-4">
                  <span className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg font-medium">
                    <Crown className="h-5 w-5" />
                    {profile.grandfather_label}
                  </span>
                </div>
              )}

              {/* ON CALL Badge */}
              {profile.is_on_call && (
                <div className="mb-4" data-testid="on-call-badge">
                  <span className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 text-emerald-800 px-4 py-2 rounded-lg font-bold">
                    <Phone className="h-5 w-5" />
                    ON CALL
                  </span>
                  <p className="text-sm text-emerald-600 mt-1">Available for urgent work</p>
                </div>
              )}

              {/* PRIORITY BOOST Badge */}
              {profile.is_priority_boosted && (
                <div className="mb-4" data-testid="priority-boost-badge">
                  <span className="inline-flex items-center gap-2 bg-orange-100 border border-orange-300 text-orange-800 px-4 py-2 rounded-lg font-bold">
                    <Crown className="h-5 w-5" />
                    PRIORITY BOOST
                  </span>
                </div>
              )}
              
              {/* Rating & Jobs */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Star className="h-6 w-6 text-yellow-400 fill-yellow-400" />
                  <span className="text-2xl font-bold text-slate-900" data-testid="profile-rating">
                    {profile.average_rating || 0}
                  </span>
                </div>
                <span className="text-slate-600">
                  · {profile.completed_jobs || 0} completed jobs
                </span>
              </div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="mb-6">
              <p className={`text-slate-700 ${!isUnlocked ? 'line-clamp-2' : ''}`} data-testid="profile-bio">
                {profile.bio}
              </p>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-sm font-medium text-slate-500 mb-1">Day Rate</p>
              <p className="text-xl font-bold text-slate-900" data-testid="profile-daily-rate">
                £{profile.day_rate || profile.daily_rate || 'N/A'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-sm font-medium text-slate-500 mb-1">Work Areas</p>
              <p className="text-sm font-semibold text-slate-900">
                {profile.work_areas?.slice(0, 2).join(', ') || 'Not specified'}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-sm font-medium text-slate-500 mb-1">Availability</p>
              <p className={`text-sm font-semibold ${
                profile.availability_mode === 'available_now' ? 'text-green-600' : 'text-slate-900'
              }`}>
                {profile.availability_preview
                  || (profile.availability_mode === 'available_now' ? 'Available Now' : 'Has availability')}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center">
              <p className="text-sm font-medium text-slate-500 mb-1">Transport</p>
              <p className="text-sm font-semibold text-slate-900">
                {profile.transport || (profile.has_transport ? 'Own transport' : 'No transport')}
              </p>
            </div>
          </div>
        </div>

        {/* FREE PROFILE REVEAL CTA — Only when not yet opened */}
        {!isUnlocked && isEmployerViewing && (
          <div className="card mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="unlock-profile-cta">
            {profile.is_on_call && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4" data-testid="on-call-premium-cost-notice">
                <p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                  <Phone className="h-4 w-4" /> This trade is currently On Call for urgent work.
                </p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-bold text-slate-900 flex items-center justify-center sm:justify-start gap-2">
                  <Unlock className="h-5 w-5 text-emerald-600" />
                  View Full Profile — Free
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  See full bio, all reviews, complete gallery, and credentials. OnSiteHand is now free to use.
                </p>
              </div>
              <button
                onClick={handleUnlockProfile}
                disabled={unlocking}
                className="btn-primary flex items-center gap-2 whitespace-nowrap bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
                data-testid="unlock-profile-btn"
              >
                {unlocking ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <Unlock className="h-4 w-4" />
                    View Full Profile
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Availability Calendar */}
        <div ref={availabilityRef} className="scroll-mt-36" data-testid="availability-section-wrapper">
          <AvailabilityCalendar
            availability={profile.availability}
            isUnlocked={isUnlocked}
          />
        </div>

        {/* Work Gallery */}
        <div ref={workDetailsRef} className="card mb-6 scroll-mt-36" data-testid="gallery-section">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Image className="h-5 w-5 text-orange-500" />
            Work Gallery
            {!profile.gallery_unlocked && profile.total_gallery_count > 2 && (
              <span className="text-sm font-normal text-slate-500">
                ({profile.total_gallery_count} photos)
              </span>
            )}
          </h3>
          
          {profile.gallery && profile.gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {profile.gallery.map((img, idx) => (
                <div key={img.url || idx} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                  <img
                    src={img.url?.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`}
                    alt={img.caption || `Work photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              
              {/* Locked gallery placeholder */}
              {!profile.gallery_unlocked && profile.total_gallery_count > 2 && (
                <div className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Lock className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-600">
                      +{profile.total_gallery_count - 2} more
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Unlock to view</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">No photos uploaded yet</p>
          )}
        </div>

        {/* Reviews Section */}
        <div ref={reviewsRef} className="card mb-6 scroll-mt-36" data-testid="reviews-section">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="h-5 w-5 text-orange-500" />
            Reviews
            <span className="text-sm font-normal text-slate-500">
              ({profile.total_reviews || 0})
            </span>
          </h3>

          {profile.reviews_unlocked ? (
            profile.reviews && profile.reviews.length > 0 ? (
              <div className="space-y-4">
                {profile.reviews.map((review, idx) => (
                  <div key={review.review_id || idx} className="border-b border-slate-100 pb-4 last:border-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const avgRating = (review.punctuality + review.professionalism + review.quality) / 3;
                          return (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${star <= avgRating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`}
                            />
                          );
                        })}
                      </div>
                      <span className="text-sm text-slate-500">
                        {new Date(review.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    {review.comment && <p className="text-slate-700">{review.comment}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No reviews yet</p>
            )
          ) : (
            <div>
              {/* Summary always visible */}
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                <span className="font-bold text-slate-900">{profile.average_rating || 0}</span>
                <span className="text-slate-500">· {profile.completed_jobs || 0} reviews</span>
              </div>
              
              {/* Highlighted quote for verified */}
              {isVerified && profile.highlighted_quote && (
                <div className="bg-slate-50 border-l-4 border-orange-400 p-4 rounded-r-lg mb-4">
                  <p className="text-slate-700 italic">"{profile.highlighted_quote}"</p>
                </div>
              )}
              
              <LockedPlaceholder message="Unlock profile to see all reviews" />
            </div>
          )}
        </div>

        {/* Credentials - Only shown when unlocked */}
        {isUnlocked && profile.credentials && profile.credentials.length > 0 && (
          <div className="card mb-6" data-testid="credentials-section">
            <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Award className="h-5 w-5 text-orange-500" />
              Credentials
            </h3>
            <p className="text-xs text-slate-500 mb-4 leading-snug">
              Where a green shield appears, OnSiteHand has checked the evidence submitted for that credential. It is not a guarantee of workmanship, conduct, availability or suitability.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.credentials.map((cred, idx) => (
                <div key={cred.credential_id || idx} className="flex items-center gap-3 bg-green-50 p-3 rounded-lg">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-slate-900">{cred.credential_type}</p>
                    {cred.reference_number && (
                      <p className="text-sm text-slate-600">Ref: {cred.reference_number}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRIMARY CTA — engagement-aware */}
        {isEmployerViewing && (
          <div ref={chatRef} className="card mb-6 bg-white border-2 border-orange-200 scroll-mt-36" data-testid="start-chat-cta">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-bold text-slate-900 flex items-center justify-center sm:justify-start gap-2">
                  <MessageSquare className="h-5 w-5 text-orange-500" />
                  {hasConversation
                    ? 'Continue Conversation'
                    : chatIntent === 'offer'
                    ? 'Send Job Offer'
                    : 'Request Quote'}
                </h3>
                <p className="text-sm text-slate-600 mt-1" data-testid="primary-cta-subtitle">
                  {hasConversation
                    ? 'You have an active conversation with this tradesperson'
                    : chatIntent === 'offer'
                    ? 'Set your dates, day rate and scope. Payment stays until the job is confirmed.'
                    : 'Send the job details so they can come back with a price. Final numbers agreed in chat.'}
                </p>
              </div>
              <button
                onClick={() => handleStartChat()}
                disabled={startingChat}
                className="btn-primary flex items-center gap-2 whitespace-nowrap"
                data-testid="start-chat-btn"
              >
                {startingChat ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4" />
                    {hasConversation
                      ? 'View Messages'
                      : chatIntent === 'offer'
                      ? 'Send Job Offer'
                      : 'Request Quote'}
                  </>
                )}
              </button>
            </div>
            {/* Free marketplace: no credits gate. Phone reveal is server-authorised. */}
            <div className="mt-4 flex items-center justify-end">
              <RevealPhoneButton userId={profile?.user_id || userId} />
            </div>
          </div>
        )}

        {/* Experience - Only when unlocked */}
        {isUnlocked && profile.experience && (
          <div className="card mb-6" data-testid="experience-section">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-orange-500" />
              Experience
            </h3>
            <p className="text-slate-700">{profile.experience}</p>
          </div>
        )}
      </div>
    </div>
  );
}
